import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Select from 'react-select';
import type { DpoRequest, DpoStats, ConsentRecord, DpoRequestStatus } from '../types';
import type { RetentionStats } from '../api/dpo';
import {
  fetchDpoStats,
  fetchSubjectRequests,
  fetchConsents,
  fetchRetentionStats,
  resolveSubjectRequest,
} from '../api/dpo';
import StatusBadge from '../components/StatusBadge';

// -------------------------------------------------------------------------
// Mock data — used as fallback when running in dev without a backend
// -------------------------------------------------------------------------

function buildMockStats(): DpoStats {
  return {
    total_requests: 12,
    pending_requests: 3,
    resolved_this_month: 5,
    average_resolution_days: 4.2,
    data_subjects_count: 38,
    anonymized_cvs_count: 7,
  };
}

function buildMockRequests(): DpoRequest[] {
  return [
    {
      id: 'dpo-001',
      request_type: 'access',
      subject_employee_id: 'emp-012',
      subject_name: 'Anna Müller',
      requester_email: 'anna.mueller@example.com',
      description: 'Request to access all personal data held on file.',
      status: 'pending',
      created_at: '2026-03-15T10:30:00Z',
    },
    {
      id: 'dpo-002',
      request_type: 'deletion',
      subject_employee_id: 'emp-007',
      subject_name: 'Jonas Weber',
      requester_email: 'jonas.weber@example.com',
      description: 'Request to delete CV and all associated match data.',
      status: 'approved',
      created_at: '2026-03-10T09:00:00Z',
      resolved_at: '2026-03-12T15:00:00Z',
      resolution_notes: 'CV anonymised; match records purged.',
    },
    {
      id: 'dpo-003',
      request_type: 'portability',
      subject_employee_id: 'emp-021',
      subject_name: 'Laura Schmidt',
      requester_email: 'laura.schmidt@example.com',
      description: 'Request for data export in machine-readable format.',
      status: 'completed',
      created_at: '2026-03-05T14:00:00Z',
      resolved_at: '2026-03-07T11:00:00Z',
    },
    {
      id: 'dpo-004',
      request_type: 'rectification',
      subject_employee_id: 'emp-033',
      subject_name: 'Felix Braun',
      requester_email: 'felix.braun@example.com',
      description: 'Skill list incorrectly extracted; requests correction.',
      status: 'pending',
      created_at: '2026-03-16T08:45:00Z',
    },
  ];
}

function buildMockConsents(): ConsentRecord[] {
  return [
    { employee_id: 'emp-001', employee_name: 'Maria Hofmann', consent_type: 'CV Processing', consented: true, consent_date: '2026-01-10' },
    { employee_id: 'emp-002', employee_name: 'Thomas Klein', consent_type: 'CV Processing', consented: true, consent_date: '2026-01-15' },
    { employee_id: 'emp-003', employee_name: 'Sabine Vogel', consent_type: 'CV Processing', consented: false },
    { employee_id: 'emp-004', employee_name: 'Markus Richter', consent_type: 'CV Processing', consented: true, consent_date: '2026-02-01', expiry_date: '2027-02-01' },
  ];
}

const IS_DEV = import.meta.env.DEV;

// -------------------------------------------------------------------------
// Page component
// -------------------------------------------------------------------------

export default function DpoDashboard() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  const [stats, setStats] = useState<DpoStats | null>(null);
  const [requests, setRequests] = useState<DpoRequest[]>([]);
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [retentionStats, setRetentionStats] = useState<RetentionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'requests' | 'consents' | 'retention'>('requests');
  const [statusFilter, setStatusFilter] = useState<DpoRequestStatus | ''>('');

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [statsData, requestsData, consentsData, retentionData] = await Promise.all([
          fetchDpoStats(),
          fetchSubjectRequests({ size: 100 }),
          fetchConsents({ size: 100 }),
          fetchRetentionStats(),
        ]);
        if (!cancelled) {
          setStats(statsData);
          setRequests(requestsData.items);
          setConsents(consentsData.items);
          setRetentionStats(retentionData);
        }
      } catch (_err) {
        if (!cancelled) {
          if (IS_DEV) {
            // Fall back to mock data in development when backend is unavailable
            setStats(buildMockStats());
            setRequests(buildMockRequests());
            setConsents(buildMockConsents());
            setRetentionStats(null);
          } else {
            setError(t('dpo.loadError'));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadData();
    return () => { cancelled = true; };
  }, [t]);

  async function handleResolve(requestId: string) {
    const notes = window.prompt(t('dpo.resolutionPrompt'));
    if (notes === null) return; // cancelled

    // Optimistic update
    setRequests((prev) =>
      prev.map((r) =>
        r.id === requestId
          ? {
              ...r,
              status: 'completed' as DpoRequestStatus,
              resolved_at: new Date().toISOString(),
              resolution_notes: notes || undefined,
            }
          : r
      )
    );

    try {
      const updated = await resolveSubjectRequest(requestId, {
        status: 'RESOLVED',
        resolution: notes || undefined,
      });
      setRequests((prev) => prev.map((r) => (r.id === requestId ? updated : r)));
    } catch (_err) {
      // Revert optimistic update on failure
      setRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? { ...r, status: 'pending' as DpoRequestStatus, resolved_at: undefined, resolution_notes: undefined }
            : r
        )
      );
      alert(t('dpo.resolveFailed'));
    }
  }

  const filteredRequests = statusFilter
    ? requests.filter((r) => r.status === statusFilter)
    : requests;

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
        <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">⟳</span>
        {t('dpo.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-red-800">
        <p className="font-medium">{t('dpo.loadErrorTitle')}</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <header>
        <h1 className="text-2xl font-bold text-gray-900">{t('dpo.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('dpo.subtitle')}
        </p>
      </header>

      {/* Compliance banner */}
      <div
        role="note"
        aria-label={t('dpo.complianceTitle')}
        className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-200 px-5 py-4"
      >
        <span className="text-blue-500 text-xl mt-0.5" aria-hidden="true">ℹ️</span>
        <div>
          <p className="text-sm font-medium text-blue-800">{t('dpo.complianceTitle')}</p>
          <p className="text-xs text-blue-700 mt-0.5">
            {t('dpo.complianceText')}
          </p>
        </div>
      </div>

      {/* KPI stats */}
      <section aria-label={t('dpo.kpiSection')} className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: t('dpo.totalRequests'),      value: stats.total_requests },
          { label: t('dpo.pending'),             value: stats.pending_requests,       color: 'text-amber-600' },
          { label: t('dpo.resolvedThisMonth'),   value: stats.resolved_this_month,    color: 'text-green-600' },
          { label: t('dpo.avgResolution'),       value: `${stats.average_resolution_days}d` },
          { label: t('dpo.dataSubjects'),        value: stats.data_subjects_count },
          { label: t('dpo.anonymisedCvs'),       value: stats.anonymized_cvs_count,   color: 'text-purple-600' },
        ].map(({ label, value, color = 'text-brand-700' }) => (
          <div key={label} className="card text-center">
            <p className="text-xs text-gray-500 leading-tight">{label}</p>
            <p className={['mt-1 text-2xl font-bold', color].join(' ')}>{value}</p>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <div>
        <div
          role="tablist"
          aria-label={t('dpo.tabs')}
          className="flex border-b border-gray-200"
        >
          {([
            ['requests', t('dpo.subjectRequests')],
            ['consents', t('dpo.consentRecords')],
            ['retention', t('dpo.dataRetention')],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              id={`tab-${id}`}
              aria-selected={activeTab === id}
              aria-controls={`panel-${id}`}
              onClick={() => setActiveTab(id)}
              className={[
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                activeTab === id
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              ].join(' ')}
            >
              {label}
              {id === 'requests' && stats.pending_requests > 0 && (
                <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                  {stats.pending_requests}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Subject Requests panel */}
        <div
          role="tabpanel"
          id="panel-requests"
          aria-labelledby="tab-requests"
          hidden={activeTab !== 'requests'}
          className="pt-4 space-y-4"
        >
          {/* Filter */}
          <div className="flex items-center gap-3">
            <label htmlFor="req-status-filter" className="text-sm text-gray-600">{t('dpo.filterStatus')}</label>
            <Select
              aria-labelledby="req-status-filter"
              options={[
                { value: '', label: t('dpo.filterAll') },
                { value: 'pending', label: t('dpo.filterPending') },
                { value: 'approved', label: t('dpo.filterApproved') },
                { value: 'rejected', label: t('dpo.filterRejected') },
                { value: 'completed', label: t('dpo.filterCompleted') },
              ]}
              value={{
                value: statusFilter,
                label: statusFilter === 'pending' ? t('dpo.filterPending') : statusFilter === 'approved' ? t('dpo.filterApproved') : statusFilter === 'rejected' ? t('dpo.filterRejected') : statusFilter === 'completed' ? t('dpo.filterCompleted') : t('dpo.filterAll'),
              }}
              onChange={(opt) => setStatusFilter((opt?.value ?? '') as DpoRequestStatus | '')}
              classNamePrefix="react-select"
              isSearchable={false}
              styles={{
                control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px', minWidth: '160px' }),
                option: (base) => ({ ...base, fontSize: '0.875rem' }),
              }}
            />
            <span className="text-xs text-gray-400 ml-auto">{t('dpo.requestCount', { count: filteredRequests.length })}</span>
          </div>

          <div className="space-y-3">
            {filteredRequests.length === 0 && (
              <p className="text-sm text-gray-500 py-6 text-center">{t('dpo.noRequests')}</p>
            )}
            {filteredRequests.map((req) => (
              <article key={req.id} className="card space-y-3">
                <header className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{req.subject_name}</p>
                    <p className="text-xs text-gray-500">{req.requester_email || req.subject_employee_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                      {t(`dpo.requestTypes.${req.request_type}`)}
                    </span>
                    <StatusBadge status={req.status} />
                  </div>
                </header>

                <p className="text-sm text-gray-700">{req.description}</p>

                <footer className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
                  <time dateTime={req.created_at}>
                    {t('dpo.received', {
                      date: new Date(req.created_at).toLocaleDateString(dateLocale, {
                        day: '2-digit', month: 'short', year: 'numeric',
                      }),
                    })}
                  </time>
                  {req.resolved_at && (
                    <time dateTime={req.resolved_at}>
                      {t('dpo.resolved', {
                        date: new Date(req.resolved_at).toLocaleDateString(dateLocale, {
                          day: '2-digit', month: 'short', year: 'numeric',
                        }),
                      })}
                    </time>
                  )}
                  {req.resolution_notes && (
                    <span className="italic">{t('dpo.note', { note: req.resolution_notes })}</span>
                  )}

                  {(req.status === 'pending' || req.status === 'approved') && (
                    <button
                      onClick={() => void handleResolve(req.id)}
                      className="ml-auto text-green-600 hover:text-green-800 font-medium focus:outline-none focus-visible:ring-1 focus-visible:ring-green-500 rounded"
                      aria-label={t('dpo.markResolvedAria', { name: req.subject_name })}
                    >
                      {t('dpo.markResolved')}
                    </button>
                  )}
                </footer>
              </article>
            ))}
          </div>
        </div>

        {/* Consent Records panel */}
        <div
          role="tabpanel"
          id="panel-consents"
          aria-labelledby="tab-consents"
          hidden={activeTab !== 'consents'}
          className="pt-4"
        >
          <div className="card overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="data-table" aria-label={t('dpo.consentTable')}>
                <thead>
                  <tr>
                    <th scope="col">{t('dpo.consentEmployee')}</th>
                    <th scope="col">{t('dpo.consentType')}</th>
                    <th scope="col">{t('dpo.consentStatus')}</th>
                    <th scope="col">{t('dpo.consentDate')}</th>
                    <th scope="col">{t('dpo.expiryDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-sm text-gray-500">
                        {t('dpo.noConsentRecords')}
                      </td>
                    </tr>
                  )}
                  {consents.map((c) => (
                    <tr key={c.employee_id}>
                      <td className="font-medium text-gray-900">{c.employee_name}</td>
                      <td>{c.consent_type}</td>
                      <td>
                        {c.consented ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                            <span aria-hidden="true">✓</span> {t('dpo.consented')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                            <span aria-hidden="true">✗</span> {t('dpo.notConsented')}
                          </span>
                        )}
                      </td>
                      <td>
                        {c.consent_date ? (
                          <time dateTime={c.consent_date}>
                            {new Date(c.consent_date).toLocaleDateString(dateLocale)}
                          </time>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td>
                        {c.expiry_date ? (
                          <time dateTime={c.expiry_date}>
                            {new Date(c.expiry_date).toLocaleDateString(dateLocale)}
                          </time>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Data Retention panel */}
        <div
          role="tabpanel"
          id="panel-retention"
          aria-labelledby="tab-retention"
          hidden={activeTab !== 'retention'}
          className="pt-4 space-y-4"
        >
          <div className="card space-y-4">
            <h2 className="text-base font-semibold text-gray-900">{t('dpo.retentionPolicy')}</h2>
            <dl className="divide-y divide-gray-100">
              {[
                { termKey: 'retentionItems.cvDocuments',       detailKey: 'retentionItems.cvDocumentsDetail' },
                { termKey: 'retentionItems.matchResults',      detailKey: 'retentionItems.matchResultsDetail' },
                { termKey: 'retentionItems.tenderRecords',     detailKey: 'retentionItems.tenderRecordsDetail' },
                { termKey: 'retentionItems.accessLogs',        detailKey: 'retentionItems.accessLogsDetail' },
                { termKey: 'retentionItems.dpoRequestRecords', detailKey: 'retentionItems.dpoRequestRecordsDetail' },
              ].map(({ termKey, detailKey }) => (
                <div key={termKey} className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-700 col-span-1">{t(`dpo.${termKey}`)}</dt>
                  <dd className="text-sm text-gray-600 col-span-2">{t(`dpo.${detailKey}`)}</dd>
                </div>
              ))}
            </dl>
            {retentionStats !== null && (
              <div className="pt-2 border-t border-gray-100">
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {[
                    { label: t('dpo.retentionPeriod'),    value: `${retentionStats.retention_days} ${i18n.resolvedLanguage === 'de' ? 'Tage' : 'days'}` },
                    { label: t('dpo.recordsDueNow'),     value: retentionStats.records_due_for_deletion },
                    { label: t('dpo.dueIn30Days'),        value: retentionStats.records_due_in30_days },
                    { label: t('dpo.erasedEmployees'),    value: retentionStats.erased_employees },
                  ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="mt-0.5 text-xl font-bold text-gray-800">{value}</p>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>

          <div className="card bg-amber-50 border-amber-200 space-y-2">
            <h3 className="text-sm font-semibold text-amber-800">{t('dpo.upcomingActions')}</h3>
            {retentionStats !== null && retentionStats.records_due_in30_days > 0 ? (
              <p className="text-sm text-amber-700">
                <Trans
                  i18nKey="dpo.recordsDueCount"
                  count={retentionStats.records_due_in30_days}
                  values={{ count: retentionStats.records_due_in30_days }}
                >
                  {'{{count}} records are due for review within the next 30 days. Navigate to '}
                  <a href="/cvs" className="underline">CV Management</a>
                  {' to review and action them.'}
                </Trans>
              </p>
            ) : (
              <p className="text-sm text-amber-700">
                <Trans
                  i18nKey="dpo.recordsDueCount"
                  count={7}
                  values={{ count: 7 }}
                >
                  {'{{count}} records are due for review within the next 30 days. Navigate to '}
                  <a href="/cvs" className="underline">CV Management</a>
                  {' to review and action them.'}
                </Trans>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
